import React, { useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { useChatAuth } from "@/context/ChatAuthContext";
import { Loader2, UserPlus, Zap } from "lucide-react";
import { toast } from "sonner";

export default function ChatRegister() {
  const { user, loading, register } = useChatAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (!loading && user) return <Navigate to="/chat" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    setSubmitting(true);
    const res = await register(email, password, name);
    setSubmitting(false);
    if (res.ok) {
      toast.success("Welcome to the chat!");
      navigate("/chat");
    } else {
      setError(res.error || "Could not create account");
    }
  };

  return (
    <div className="min-h-screen grain flex items-center justify-center px-6 py-20" data-testid="chat-register">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-10">
          <span className="inline-flex items-center justify-center w-11 h-11 border border-yellow-400/40 bg-yellow-400/5">
            <Zap className="w-6 h-6 text-yellow-400" strokeWidth={2.5} />
          </span>
          <div className="font-heading">
            <div className="text-white font-black text-xl uppercase tracking-tight leading-none">Vortex Ghost</div>
            <div className="text-[10px] tracking-[0.28em] text-zinc-500 font-mono uppercase mt-1">Storm Comms Channel</div>
          </div>
        </div>

        <div className="card-vg p-8">
          <div className="overline">Create Account</div>
          <h1 className="font-heading text-3xl font-bold mt-3 flex items-center gap-3">
            <UserPlus className="w-6 h-6 text-yellow-400" /> Join the chat
          </h1>

          <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="chat-register-form">
            <div>
              <label className="overline mb-2 block !text-zinc-400">Display Name</label>
              <input value={name} onChange={(e) => setName(e.target.value)} className="input-vg" required data-testid="chat-register-name" />
            </div>
            <div>
              <label className="overline mb-2 block !text-zinc-400">Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="input-vg" required data-testid="chat-register-email" />
            </div>
            <div>
              <label className="overline mb-2 block !text-zinc-400">Password (min 6)</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="input-vg" required minLength={6} data-testid="chat-register-password" />
            </div>

            {error && (
              <div className="text-red-400 text-sm border border-red-500/30 bg-red-500/5 p-3" data-testid="chat-register-error">{error}</div>
            )}

            <button type="submit" disabled={submitting} className="btn-primary w-full justify-center disabled:opacity-50" data-testid="chat-register-submit">
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating…</> : "Create account"}
            </button>
          </form>

          <div className="mt-6 text-center text-sm text-zinc-400">
            Already have one?{" "}
            <Link to="/chat-login" className="text-yellow-400 hover:underline" data-testid="chat-login-link">Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
